import React, { useEffect, useState, useCallback } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Ban } from "lucide-react";

const STATUSES = ["", "queued", "sent", "delivered", "opened", "clicked", "registered", "verified", "submitted", "bounced", "expired", "revoked"];

const statusColor = (s) => ({
  queued: "bg-slate-100 text-slate-700", sent: "bg-sky-100 text-sky-800",
  delivered: "bg-sky-100 text-sky-800", opened: "bg-violet-100 text-violet-800",
  clicked: "bg-violet-100 text-violet-800", registered: "bg-emerald-100 text-emerald-800",
  verified: "bg-emerald-200 text-emerald-900", submitted: "bg-emerald-600 text-white",
  bounced: "bg-amber-100 text-amber-900", expired: "bg-slate-200 text-slate-600",
  revoked: "bg-rose-100 text-rose-800",
}[s] || "bg-slate-100 text-slate-700");

const AdminReferrals = () => {
  const { t, tCountry, tProfile } = useLang();
  const [filters, setFilters] = useState({ status: "", country: "", profile: "", language: "" });
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data || [])).catch(() => setProfiles([]));
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("limit", "200");
      const [a, b] = await Promise.all([
        api.get(`/admin/referrals/all?${q.toString()}`).then((r) => r.data.invitations || []),
        api.get("/admin/referrals/funnel").then((r) => r.data),
      ]);
      setRows(a);
      setFunnel(b);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load failed"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const revoke = async (id) => {
    if (!window.confirm(t("referrals.admin.revokeConfirm"))) return;
    try {
      await api.post(`/admin/referrals/revoke/${id}`);
      toast.success(t("referrals.admin.revoked"));
      fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, "revoke failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-referrals-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-referrals-title">
          {t("referrals.admin.heading")}
        </h1>
      </div>

      {/* Funnel */}
      {funnel && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-referrals-funnel">
          {["sent", "opened", "clicked", "registered", "verified", "submitted"].map((k) => (
            <Card key={k}>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase">{t(`referrals.funnel.${k}`)}</div>
                <div className="text-2xl font-bold text-emerald-700" data-testid={`admin-referrals-funnel-${k}`}>{funnel[k] ?? 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader><CardTitle className="text-sm">{t("referrals.admin.filters")}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-5 gap-2">
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="admin-referrals-filter-status"
          >
            <option value="">{`— ${t("referrals.admin.filterStatus")} —`}</option>
            {STATUSES.slice(1).map((s) => <option key={s} value={s}>{t(`referrals.status.${s}`)}</option>)}
          </select>
          <select
            value={filters.country}
            onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="admin-referrals-filter-country"
          >
            <option value="">{`— ${t("referrals.admin.filterCountry")} —`}</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{`${c.code} — ${tCountry(c.code, c)}`}</option>
            ))}
          </select>
          <select
            value={filters.profile}
            onChange={(e) => setFilters((f) => ({ ...f, profile: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="admin-referrals-filter-profile"
          >
            <option value="">{`— ${t("referrals.admin.filterProfile")} —`}</option>
            {profiles.map((p) => (
              <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
            ))}
          </select>
          <select
            value={filters.language}
            onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="admin-referrals-filter-language"
          >
            <option value="">{`— ${t("referrals.admin.filterLanguage")} —`}</option>
            {LANGUAGES.filter((Lg) => Lg.isActive).map((Lg) => (
              <option key={Lg.code} value={Lg.code}>{`${Lg.code.toUpperCase()} — ${Lg.nameNative}`}</option>
            ))}
          </select>
          <Button onClick={fetchAll} disabled={loading} data-testid="admin-referrals-apply">
            {loading ? "…" : t("referrals.admin.apply")}
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          {rows.length === 0 ? (
            <p className="p-6 text-slate-500 text-sm" data-testid="admin-referrals-empty">
              {t("referrals.admin.empty")}
            </p>
          ) : (
            <table className="min-w-full text-sm" data-testid="admin-referrals-table">
              <thead>
                <tr className="text-start text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3">{t("referrals.admin.colInviter")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colTarget")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colProfile")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colLang")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colStatus")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colCreated")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100" data-testid={`admin-referral-row-${inv.id}`}>
                    <td className="py-2 px-3 font-mono text-xs text-slate-500">{(inv.inviter_first_name || "—")}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inv.target_email_masked}</td>
                    <td className="py-2 px-3">{inv.target_profile_hint || "—"}</td>
                    <td className="py-2 px-3 uppercase">{inv.target_language_hint || "—"}</td>
                    <td className="py-2 px-3">
                      <Badge className={statusColor(inv.status)}>{t(`referrals.status.${inv.status}`)}</Badge>
                    </td>
                    <td className="py-2 px-3 text-slate-500 text-xs">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 px-3">
                      {!["revoked", "expired"].includes(inv.status) && (
                        <button
                          onClick={() => revoke(inv.id)}
                          className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 text-xs"
                          data-testid={`admin-referrals-revoke-${inv.id}`}
                        >
                          <Ban className="w-3.5 h-3.5" /> {t("referrals.admin.revoke")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReferrals;
